// pages/Devices/deviceadd.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createDevice,
  getSerialNumber,
  clearUpdateState,
  selectCreateLoading,
  selectCreateSuccess,
  selectDevicesError,
  clearSuccessMessage
} from '../../features/devices/deviceSlice';
import { deviceAPI } from '../../features/devices/deviceAPI';
import { officeAPI } from '../../features/offices/officesAPI';
import SearchableDealerSelect from './SearchableDealerSelect';
import Select from 'react-select';

const US_STATES = [
  "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DE", "DC",
  "FM", "FL", "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS",
  "KY", "LA", "ME", "MH", "MD", "MA", "MI", "MN", "MS", "MO",
  "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "MP",
  "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC", "SD", "TN",
  "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY",
  "INDIA", "INDIAV2", "INDIAV3","INDIAV4","CAV4","INDIAMT", "INDIAMTPL", "INDIASE", "BZ"
];

const DeviceAdd = ({ isOpen, onClose, dealers, isAdmin }) => {
  const dispatch = useDispatch();
  const createLoading = useSelector(selectCreateLoading);
  const createSuccess = useSelector(selectCreateSuccess);
  const createError = useSelector(selectDevicesError);
  const loggedInUser = useSelector((state) => state.auth.user);
  const loggedInDealerId = loggedInUser?.dealerId || loggedInUser?.dealerid || deviceAPI.getDealerId();
  const requiresRegionSelection = Number(loggedInDealerId) === 31278;


  
  const [formData, setFormData] = useState({
    snostate: '',
    sno: '',
    dealerName: '',
    dealerId: '',
    officeId: '',
    devId: '',
    cbid: '',
    schipid: '',
    cchipid: '',
    status: 'Registered',
    lastsentdate: 'A200',
    calibrationdate: '',
    registeredname: '',
    cam4: ''
  });
  
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [offices, setOffices] = useState([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [dealerSearch, setDealerSearch] = useState('');
  const [officeSearch, setOfficeSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const availableStates = requiresRegionSelection
    ? (selectedCountry === 'INDIA' ? ['INDIA', 'INDIAV4'] : ['CA', 'CAV4'])
    : US_STATES;
  
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      dispatch(clearUpdateState());
      dispatch(clearSuccessMessage());
    }
  }, [isOpen, dispatch]);
  
  // Handle success - close modal after success
  useEffect(() => {
    if (createSuccess) {
      showAlert('success', 'Device added successfully!');
      // Close modal after a short delay
      setTimeout(() => {
        handleClose();
      }, 1500);
    }
  }, [createSuccess]);
  
  // Handle error
  useEffect(() => {
    if (createError) {
      showAlert('error', createError);
      dispatch(clearUpdateState());
    }
  }, [createError, dispatch]);
  
  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => {
      setAlert({ show: false, type: '', message: '' });
    }, 3000);
  };
  
  const resetForm = () => {
    setSelectedCountry('');
    setFormData({
      snostate: '',
      sno: '',
      dealerName: '',
      dealerId: '',
      officeId: '',
      devId: '',
      cbid: '',
      schipid: '',
      cchipid: '',
      status: 'Registered',
      lastsentdate: 'A200U',
      calibrationdate: new Date().toLocaleString(),
      registeredname: '',
      cam4: ''
    });
    setErrors({});
    setDealerSearch('');
    setOfficeSearch('');
  };
  
  // Ignore responses for a previous state or a closed dialog.
  useEffect(() => {
    if (!isOpen || !formData.snostate) return;
    let cancelled = false;
    const state = formData.snostate;
    setFormData(prev => ({ ...prev, sno: '' }));
    dispatch(getSerialNumber(state)).unwrap()
      .then(value => {
        const nextNum = parseInt(value, 10) + 1;
        if (!Number.isFinite(nextNum)) throw new Error('Invalid serial number response');
        if (!cancelled) {
          setFormData(prev => ({ ...prev, sno: `612${state}${String(nextNum).padStart(5, '0')}` }));
        }
      })
      .catch(() => {
        if (!cancelled) setErrors(prev => ({ ...prev, sno: 'Unable to load serial number. Please select the state again.' }));
      });
    return () => { cancelled = true; };
  }, [isOpen, formData.snostate, dispatch]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.snostate || !availableStates.includes(formData.snostate)) newErrors.snostate = 'Select a valid state';
    if (!formData.sno) newErrors.sno = 'SNO is required';
    if (isAdmin && !formData.dealerName) newErrors.dealerName = 'Dealer is required';
    if (!formData.devId) newErrors.devId = 'Device ID is required';
    if (!formData.cbid) newErrors.cbid = 'Control Board ID is required';
    if (!formData.status) newErrors.status = 'Status is required';
    if (!formData.registeredname?.trim()) newErrors.registeredname = 'Registered name is required';

    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value, ...(field === 'snostate' ? { sno: '' } : {}) }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const handleDealerChange = (dealerName) => {
    setDealerSearch(dealerName);
    const selectedDealer = dealers.find(d => d.dealerName === dealerName);
    if (selectedDealer) {
      setFormData(prev => ({
        ...prev,
        dealerName: selectedDealer.dealerName,
        dealerId: selectedDealer.dealerId,
        officeId: ''
      }));
      setOfficeSearch('');
    } else {
      setFormData(prev => ({ ...prev, dealerName: '', dealerId: '', officeId: '' }));
    }
  };

  const handleOfficeChange = (officeName) => {
    setOfficeSearch(officeName);
    const selectedOffice = offices.find(office => office.officeName === officeName);
    handleInputChange('officeId', selectedOffice ? (selectedOffice.officeId || selectedOffice.id) : '');
  };

  useEffect(() => {
    if (!isOpen) return;
    const dealerId = isAdmin ? formData.dealerId : loggedInDealerId;

    setOfficesLoading(true);
    const request = dealerId
      ? officeAPI.fetchDealerOffices(dealerId)
      : (isAdmin ? officeAPI.fetchOffices() : Promise.resolve([]));

    request
      .then((data) => setOffices(Array.isArray(data) ? data : []))
      .catch(() => setOffices([]))
      .finally(() => setOfficesLoading(false));
  }, [isOpen, isAdmin, formData.dealerId, loggedInDealerId]);
  
  // Create FormData for add device
  const loadFormData = () => {
    const fd = new FormData();
    fd.append("sno", formData.sno);
    fd.append("devId", formData.devId);
    fd.append("cbid", formData.cbid);
    fd.append("schipid", formData.schipid);
    fd.append("cchipid", formData.cchipid);
    fd.append("dealer", formData.dealerId || loggedInDealerId || '');
    if (formData.officeId) fd.append("office", formData.officeId);
    fd.append("calibrationdate", formData.calibrationdate || new Date().toLocaleString());
    fd.append("registeredname", formData.registeredname);
    fd.append("status", formData.status);
    fd.append("cam4", formData.cam4);
    fd.append("lastiddownload", 0);
    fd.append("lastsentdate", formData.lastsentdate);
    fd.append("iswifi", 0);
    fd.append("autotimeset", 1);
    fd.append("timezone", 'GMT');
    fd.append("lastoperation", '');
    fd.append("lastopdate", '');
    return fd;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const deviceData = loadFormData();
    await dispatch(createDevice(deviceData));
  };
  
  // Handle manual close - reset everything
  const handleClose = () => {
    dispatch(clearUpdateState());
    dispatch(clearSuccessMessage());
    resetForm();
    setAlert({ show: false, type: '', message: '' });
    onClose();
  };
  
  if (!isOpen) return null;

if (requiresRegionSelection && !selectedCountry) {
  return (
    <div className="modal-overlay">
      <div className="edit-device-modal country-select-modal" role="dialog" aria-modal="true" aria-label="Select device region">
        <div className="modal-header">
          <h2>Add Device</h2>

          <button
            className="close-btn"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        <div className="country-select-body">
          <p>Select Device Region</p>

          <div className="country-select-buttons">

            <button
              type="button"
              onClick={() => {
                setSelectedCountry('INDIA');
                handleInputChange('snostate', 'INDIAV4');
              }}
            >
              INDIA
            </button>

            <button
              type="button"
              onClick={() => {
                setSelectedCountry('US');
                handleInputChange('snostate', 'CAV4');
              }}
            >
              US
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}

return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="edit-device-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Device</h2>
          <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">&times;</button>
        </div>
        
        {alert.show && (
          <div className={`alert-banner ${alert.type}`}>
            <span>{alert.message}</span>
            <button className="alert-close" onClick={() => setAlert({ show: false })}>✕</button>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Select State <span className="required">*</span></label>
                <div className="select-wrapper">
                <Select
                  aria-label="Select State"
                  options={availableStates.map(state => ({ value: state, label: state }))}
                  value={formData.snostate ? { value: formData.snostate, label: formData.snostate } : null}
                  onChange={option => handleInputChange('snostate', option?.value || '')}
                  className={errors.snostate ? 'error' : ''}
                  classNamePrefix="state-select"
                  placeholder="Search & select state..."
                  isSearchable
                  isDisabled={createLoading}
                />
 
                </div>
                {errors.snostate && <span className="error-message">{errors.snostate}</span>}
              </div>
              
              <div className="form-group">
                <label>SNO <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.sno}
                  className={errors.sno ? 'error' : ''}
                  readOnly
                  disabled={createLoading}
                />
                {errors.sno && <span className="error-message">{errors.sno}</span>}
              </div>
            </div>
            
            {isAdmin && (
              <div className="form-group">
                <label>Select Dealer <span className="required">*</span></label>
                <SearchableDealerSelect
                  dealers={dealers || []}
                  value={dealerSearch}
                  onSelect={handleDealerChange}
                  hasError={Boolean(errors.dealerName)}
                  disabled={createLoading}
                />
                {errors.dealerName && <span className="error-message">{errors.dealerName}</span>}
              </div>
            )}

            <div className="form-group">
              <label>Select Office</label>
              <SearchableDealerSelect
                dealers={offices}
                value={officeSearch}
                onSelect={handleOfficeChange}
                getLabel={(office) => office.officeName}
                getKey={(office) => office.officeId || office.id || office.officeName}
                disabled={createLoading || officesLoading || (isAdmin && !formData.dealerId)}
                placeholder={officesLoading ? 'Loading officers...' : (isAdmin && !formData.dealerId ? 'Select a dealer first' : 'Search office')}
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Device ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.devId}
                  onChange={(e) => handleInputChange('devId', e.target.value)}
                  className={errors.devId ? 'error' : ''}
                  placeholder="Enter Device ID"
                  disabled={createLoading}
                />
                {errors.devId && <span className="error-message">{errors.devId}</span>}
              </div>
              
              <div className="form-group">
                <label>Control Board ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.cbid}
                  onChange={(e) => handleInputChange('cbid', e.target.value)}
                  className={errors.cbid ? 'error' : ''}
                  placeholder="Enter Control Board ID"
                  disabled={createLoading}
                />
                {errors.cbid && <span className="error-message">{errors.cbid}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Sampler Chip ID</label>
                <input
                  type="text"
                  value={formData.schipid}
                  onChange={(e) => handleInputChange('schipid', e.target.value)}
                  placeholder="Enter Sampler Chip ID"
                  disabled={createLoading}
                />
              </div>

              <div className="form-group">
                <label>CB Chip ID</label>
                <input
                  type="text"
                  value={formData.cchipid}
                  onChange={(e) => handleInputChange('cchipid', e.target.value)}
                  placeholder="Enter CB Chip ID"
                  disabled={createLoading}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="add-device-ownership">Ownership Type</label>
              <div className="select-wrapper">
                <select
                  id="add-device-ownership"
                  value={formData.cam4}
                  onChange={(e) => handleInputChange('cam4', e.target.value)}
                  disabled={createLoading}
                >
                  <option value="">Select Ownership Type</option>
                  <option value="Rental">Rental</option>
                  <option value="Purchased">Purchased</option>
                  <option value="AMC">AMC</option>
                </select>
                <span className="status-down-arrow">&#9660;</span>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Device Mode <span className="required">*</span></label>
                <div className='select-wrapper'>
                <select
                  value={formData.lastsentdate}
                  onChange={(e) => handleInputChange('lastsentdate', e.target.value)}
                  disabled={createLoading}
                >
                  <option value="A200">A200</option>
                  <option value="A300I">A300I</option>
                  <option value="A300U">A300U</option>
                  <option value="GPS">GPS</option>
                  <option value="A300O">A300O</option>
                  <option value="A200U">A200U</option>
                  <option value="A200I">A200I</option>
                </select>
                   <span className="status-down-arrow">&#9660;</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>Status <span className="required">*</span></label>
                                <div className='select-wrapper'>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className={errors.status ? 'error' : ''}
                  disabled={createLoading}
                >
                  <option value="Registered">Registered</option>
                  <option value="UnderTesting">Under Testing</option>
                  <option value="Testcompleted">Test Completed</option>
                  <option value="readytoinstall">Ready to install</option>
                  <option value="Installed">Installed</option>
                  <option value="Removed">Removed</option>
                  <option value="NotAssigned">NotAssigned</option>
                  <option value="Trashed">Trashed</option>
                </select>
                   <span className="status-down-arrow">&#9660;</span>
                </div>
                {errors.status && <span className="error-message">{errors.status}</span>}
              </div>

              {/* <div className="form-group">
                <label>Control Board ID <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.cbid}
                  onChange={(e) => handleInputChange('cbid', e.target.value)}
                  className={errors.cbid ? 'error' : ''}
                  placeholder="Enter Control Board ID"
                  disabled={createLoading}
                />
                {errors.cbid && <span className="error-message">{errors.cbid}</span>}
              </div> */}
             

            <div className="form-group">
  <label>Registered Name <span className="required">*</span></label>
  <input
    value={formData.registeredname}
    onChange={(e) => handleInputChange('registeredname', e.target.value)}
    className={errors.registeredname ? 'error' : ''}
    placeholder="Enter registered name"
    disabled={createLoading}
  />
  {errors.registeredname && <span className="error-message">{errors.registeredname}</span>}
</div>



            </div>
          </div>
          
          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={handleClose} disabled={createLoading}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={createLoading}>
              {createLoading ? 'Saving...' : 'Save Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceAdd;
